/* eslint-disable eqeqeq */
import { Download, Loader2 } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { CalendarDatePicker } from "../../../components/CalendarDatePicker";
import MainBody from "../../../components/MainBody/MainBody";
import DropDownButton from "../../../components/DropDownButton";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "../../../components/use-media-query";
import {
  API,
  defaultLimitPagination,
  fileUpload,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import {
  formatNumber,
  formatSubcontractorName,
  getModuleNamingBySlug,
  pastFinancialdate,
} from "../../../lib/Misc";
import moment from "moment";
import sortCaret from "../../../components/SortCaret";
import VendorSelect from "../../../components/VendorSelect";
import { useOrganization } from "../../../lib/context/OrganisationContext";

const VendorSummary = ({ trustId, id, vendor }) => {
  const { organizationData } = useOrganization();

  const location = useLocation();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const query = new URLSearchParams(location.search);
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vendorId, setVendorId] = useState(id);
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("ASC");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const fromDate = query.get("from")
    ? new Date(query.get("from")?.split("-").reverse().join("-"))
    : "";
  const toDate = query.get("to")
    ? new Date(query.get("to")?.split("-").reverse().join("-"))
    : "";
  const [selectedDateRange, setSelectedDateRange] = useState(
    fromDate && toDate
      ? {
          from: fromDate,
          to: toDate,
        }
      : {
          from: pastFinancialdate[0],
          to: pastFinancialdate[1],
        }
  );

  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorListing, setVendorListing] = useState([]);

  const [vendorList, setVendorList] = useState(
    vendor
      ? [
          {
            ...vendor,
            value: vendor.id,
            label: formatSubcontractorName(vendor),
          },
        ]
      : []
  );

  const fetchVendorItem = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const formData = new FormData();

        formData.append(`vendor_id`, vendorId);
        formData.append("trust_id", trustId ? trustId : "");
        formData.append(
          "limit",
          passLimit ? passLimit : limit ? limit : defaultLimitPagination
        );
        formData.append("page", passPage ? passPage : page ? page : 1);
        formData.append("sort_column", sortField ? sortField : "");
        formData.append("sort_order", sortOrder ? sortOrder : "");

        formData.append(
          "from",
          moment(selectedDateRange?.from).format("DD-MM-YYYY")
            ? moment(selectedDateRange?.from).format("DD-MM-YYYY")
            : ""
        );
        formData.append(
          "to",
          moment(selectedDateRange?.to).format("DD-MM-YYYY")
            ? moment(selectedDateRange?.to).format("DD-MM-YYYY")
            : ""
        );
        formData.append("export", 0);
        const { data } = await fileUpload(
          `${API.VENDOR_STATEMENT_REPORT_DETAIL}`,
          formData
        );
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        setReportData(data?.data?.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setReportData([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      vendorId,
      trustId,
      // limit,
      // page,
      sortField,
      sortOrder,
      selectedDateRange?.from,
      selectedDateRange?.to,
    ]
  );

  useEffect(() => {
    fetchVendorItem();
  }, [fetchVendorItem]);

  const reportGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: reportData[i]?.id ? reportData[i]?.id : "",
        transaction_date: reportData[i]?.transaction_date,
        audit_number: reportData[i]?.audit_number,
        description: reportData[i]?.description
          ? reportData[i]?.description
          : "-",
        debit: (
          <span className="text-red-500">
            {reportData[i]?.transaction_type == 0
              ? `$${formatNumber(reportData[i]?.amount)}`
              : `-`}
          </span>
        ),
        credit: (
          <span className="text-green-500">
            {reportData[i]?.transaction_type == 1
              ? `$${formatNumber(reportData[i]?.amount)}`
              : `-`}
          </span>
        ),
      });
    }
    return items;
  };

  const reportDatas = reportGenerator(reportData?.length);

  const columns = [
    {
      dataField: "transaction_date",
      text: "Date",
      headerClasses: THClasses,
      classes: TDClasses,
      sort: true,
      sortCaret,
    },
    {
      dataField: "audit_number",
      text: "Audit No.",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "description",
      text: "Description",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "debit",
      text: "Debit",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "credit",
      text: "Credit",
      headerClasses: THClasses,
      classes: TDClasses,
    },
  ];

  const handleExport = async (format) => {
    if (trustId) {
      try {
        setLoading(true);
        const formData = new FormData();

        formData.append(`vendor_id`, vendorId);

        formData.append("trust_id", trustId ? trustId : "");

        formData.append(
          "from",
          moment(selectedDateRange?.from).format("DD-MM-YYYY")
            ? moment(selectedDateRange?.from).format("DD-MM-YYYY")
            : ""
        );
        formData.append(
          "to",
          moment(selectedDateRange?.to).format("DD-MM-YYYY")
            ? moment(selectedDateRange?.to).format("DD-MM-YYYY")
            : ""
        );

        formData.append("export", 1);
        formData.append("format", format);

        const { data } = await fileUpload(
          `${API.VENDOR_STATEMENT_REPORT_DETAIL}`,
          formData
        );
        const export_report_path = data?.data?.file;
        if (export_report_path) {
          var a = document.createElement("a");
          a.href = export_report_path;
          var file = export_report_path.split("/");
          a.setAttribute(
            "download",
            file[file.length - 1] || "transactionsreport.pdf"
          );
          a.setAttribute("target", "_blank");
          document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
          a.click();
          a.remove();
          setLoading(false);
        }
      } catch (e) {
        setLoading(false);
      }
    }
  };

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchVendorItem(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchVendorItem(limit, page);
  };

  useEffect(() => {
    if (vendorList) {
      let vendorTempListing = vendorList?.map((item) => ({
        ...item,
        value: item.id,
        label: `${formatSubcontractorName(item)}${
          item?.code ? ` (${item?.code})` : ""
        }`,
      }));
      setVendorListing(vendorTempListing || []);
    } else {
      setVendorListing([]);
    }
  }, [vendorList]);

  return (
    <div className="relative">
      <MainBody
        search={false}
        actionAlign={"between"}
        actionContent={
          <>
            <div className="min-w-0 flex items-center gap-4">
              <div className="relative">
                <VendorSelect
                  trustId={trustId}
                  frameworkData={vendorListing?.length > 0 ? vendorListing?.filter((item) => item?.value !== "new") : []} 
                  optionType={getModuleNamingBySlug(
                    organizationData?.organizationData,
                    "vendors",
                    "Vendor"
                  )}
                  handleSelect={(e) => {
                    setVendorId(e);
                  }}
                  name="vendor_id"
                  disabled={false}
                  initialValue={vendorId ? vendorId : ""}
                  setVendorList={setVendorList} // Update vendor list as new data loads
                  setVendorLoading={setVendorLoading} // Control loading state
                  onClear={() => {
                    setVendorId("");
                  }}
                  loading={vendorLoading}
                  clearable={false}
                />
                {vendorLoading && (
                  <span className="absolute top-2 right-2 animate-spin">
                    <Loader2 width={20} height={20} />
                  </span>
                )}
              </div>
              <div>
                Closing Balance:{" "}
                {vendorListing?.find((item) => item.id === vendorId)
                  ?.closing_balance
                  ? vendorListing?.find((item) => item.id === vendorId)
                      ?.closing_balance > 0
                    ? `($${formatNumber(
                        Number(
                          vendorListing?.find((item) => item.id === vendorId)
                            ?.closing_balance
                        )
                      )})`
                    : "$" +
                      formatNumber(
                        Number(
                          Math.abs(
                            vendorListing?.find((item) => item.id === vendorId)
                              ?.closing_balance
                          )
                        )
                      )
                  : "$0.00"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CalendarDatePicker
                date={selectedDateRange}
                onDateSelect={setSelectedDateRange}
                variant="outline"
                align={isDesktop ? "end" : "start"}
                disabledDate={(date) => date > new Date()}
              />

              <DropDownButton
                items={[
                  {
                    label: "PDF",
                    onClick: () => handleExport("pdf"),
                    disabled: loading,
                  },
                  {
                    label: "Excel",
                    onClick: () => handleExport("xlsx"),
                    disabled: loading,
                  },
                ]}
                text={
                  <div className="flex gap-2 items-center">
                    Export <Download size={18} />
                  </div>
                }
              />
            </div>
          </>
        }
        expandList={true}
        className="!p-0 !bg-background"
        loading={loading}
      >
        <div className="custom-table">
          <div className={"table-body-class"}>
            <BootstrapTable
              keyField="key"
              remote
              data={reportDatas}
              columns={columns}
              // selectRow={selectRow}
              onTableChange={handleTableChange}
              rowClasses={(row) => {
                if (row?.balanceRow) {
                  return `${TRClasses} font-bold dark:bg-muted bg-webSecondary dark:hover:!bg-muted hover:!bg-webSecondary dark:text-foreground text-white`;
                }
                return TRClasses;
              }}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>
          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>
    </div>
  );
};

export default VendorSummary;
